import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import './MeetAndLearn.css';

const MeetAndLearn = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/meet-learn`)
      .then(res => res.json())
      .then(data => {
        setSessions(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading sessions:', error);
        setLoading(false);
      });
  }, []);

  const sectionStyle = {
    marginBottom: '3rem',
    padding: '2rem',
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    border: '1px solid #e2e8f0'
  };

  return (
    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 className="page-title" style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '1rem' }}>
          🎓 Meet and Learn
        </h1>
        <p className="page-subtitle" style={{ fontSize: '1.2rem', color: '#64748b', maxWidth: '600px', margin: '0 auto' }}>
          Learning opportunities, networking events, and professional development for EBTA members and solution-focused practitioners
        </p>
      </div>

      {/* Introduction */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginRight: '1rem' }}>🤝</div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Learning & Networking</h2>
        </div>
        <p style={{ color: '#64748b', fontSize: '1.1rem', lineHeight: '1.6' }}>
          EBTA's Meet and Learn activities provide ongoing opportunities for professional development, knowledge sharing,
          and networking within the solution-focused community. These events complement our annual conferences with
          regular learning opportunities throughout the year.
        </p>
      </div>

      {/* Upcoming Sessions */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginRight: '1rem' }}>📅</div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Upcoming Meet & Learn Sessions</h2>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>Loading sessions...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
            {sessions.map((session, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: '#ffffff',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Session Header with Photo */}
                <div style={{
                  display: 'flex',
                  gap: '1.5rem',
                  marginBottom: '1.5rem',
                  alignItems: 'flex-start'
                }}>
                  {/* Session Image - Small in top left */}
                  <div style={{
                    width: '140px',
                    height: '180px',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    {session.imageUrl ? (
                      <img
                        src={session.imageUrl}
                        alt={session.facilitator}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center top'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '2rem' }}>👤</div>
                    )}
                  </div>

                  {/* Session Info */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: '#1e293b', fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: 0 }}>
                      {session.facilitator}
                    </h3>

                    <div style={{
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      display: 'inline-block',
                      marginBottom: '1rem'
                    }}>
                      {session.date ? new Date(session.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : `${session.month} ${session.year}`}
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div style={{ marginBottom: '1rem' }}>
                  {session.topic !== 'TBA' && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#374151' }}>Topic:</strong>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>{session.topic}</p>
                    </div>
                  )}

                  {session.description && session.description !== 'Details coming soon' && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#374151' }}>Description:</strong>
                      <div
                        className="ml-description"
                        style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}
                        dangerouslySetInnerHTML={{ __html: session.description }}
                      />
                    </div>
                  )}

                  {session.time !== 'TBA' && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#374151' }}>Time:</strong>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>{session.time}</p>
                    </div>
                  )}

                  {session.audience && session.audience !== 'Open to all' && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#374151' }}>For:</strong>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>{session.audience}</p>
                    </div>
                  )}

                  {session.registration && session.registration !== 'Information to follow' && (
                    <div>
                      <strong style={{ color: '#374151' }}>Registration:</strong>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>{session.registration}</p>
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                {(session.topic === 'TBA' || !session.description || session.description === 'Details coming soon') && (
                  <div style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fde047',
                    color: '#92400e',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    📝 Details coming soon
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* How to Participate */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginRight: '1rem' }}>📋</div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>How to Participate</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ color: '#2563eb', fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>👥</span>
              EBTA Members
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '1rem' }}>
              EBTA members receive priority access to all Meet and Learn activities, including exclusive member-only sessions
              and early registration for popular workshops.
            </p>
            <Link
              to="/membership"
              style={{
                color: '#2563eb',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Become a Member →
            </Link>
          </div>

          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ color: '#059669', fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>🌍</span>
              Open Sessions
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '1rem' }}>
              Many of our learning sessions are open to all solution-focused practitioners, regardless of membership status.
              These sessions welcome newcomers to the SF community.
            </p>
            <Link
              to="/contact"
              style={{
                color: '#059669',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Get Information →
            </Link>
          </div>

          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ color: '#d97706', fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>📢</span>
              Stay Updated
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '1rem' }}>
              Follow our announcements and newsletters to stay informed about upcoming Meet and Learn opportunities,
              registration deadlines, and special events.
            </p>
            <Link
              to="/contact"
              style={{
                color: '#d97706',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Subscribe to Updates →
            </Link>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginRight: '1rem' }}>✨</div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Benefits of Participation</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div style={{ backgroundColor: '#f0f9ff', padding: '1rem', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🧠</div>
            <h4 style={{ color: '#1e40af', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>Continuous Learning</h4>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>Stay updated with latest developments in SF practice</p>
          </div>

          <div style={{ backgroundColor: '#f0fdf4', padding: '1rem', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🤝</div>
            <h4 style={{ color: '#166534', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>Professional Network</h4>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>Connect with practitioners across Europe and beyond</p>
          </div>

          <div style={{ backgroundColor: '#fefce8', padding: '1rem', borderRadius: '6px', border: '1px solid #fde047' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🎯</div>
            <h4 style={{ color: '#a16207', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>Skill Enhancement</h4>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>Develop new and refine existing skills</p>
          </div>

          <div style={{ backgroundColor: '#fdf4ff', padding: '1rem', borderRadius: '6px', border: '1px solid #e9d5ff' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>💡</div>
            <h4 style={{ color: '#7c2d12', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>Knowledge Sharing</h4>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>Share experiences and learn from case discussions</p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div style={{ ...sectionStyle, backgroundColor: '#f1f5f9', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginRight: '1rem' }}>🚀</div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Join Our Learning Community</h2>
        </div>
        <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
          Be part of a vibrant community of solution-focused professionals committed to continuous learning and development.
        </p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link
            to="/membership"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '1rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1d4ed8';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
          >
            <span style={{ marginRight: '0.5rem' }}>👥</span>
            Become a Member
          </Link>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: '#059669',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '1rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#047857';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#059669';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
          >
            <span style={{ marginRight: '0.5rem' }}>📧</span>
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MeetAndLearn;
